import React, { useEffect, useMemo, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import clsx from "clsx";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, TimeScale);

// ---------------------- helpers: number formatting ----------------------
const pretty = (n) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : (Number(n || 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const money = (n) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { style: "currency", currency: "KES", currencyDisplay: "code", maximumFractionDigits: 0 })
    : (Number(n || 0) || 0).toLocaleString(undefined, { style: "currency", currency: "KES", currencyDisplay: "code", maximumFractionDigits: 0 });

// ---------------------- helper: make YYYY-MM-DD in LOCAL time ----------------------
const ymdLocal = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).toLocaleDateString("en-CA"); // e.g., 2025-08-09

const DashboardOverview = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date range state
  const [range, setRange] = useState("month"); // "today" | "week" | "month" | "custom"
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // ---------------------- build query string (LOCAL dates; no toISOString) ----------------------
  const q = useMemo(() => {
    const now = new Date();
    let start = "", end = "";

    if (range === "today") {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      start = ymdLocal(d);
      end = ymdLocal(d);
    } else if (range === "week") {
      const day = now.getDay(); // 0 Sun
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((day + 6) % 7));
      start = ymdLocal(monday);
      end = ymdLocal(now);
    } else if (range === "month") {
      const d1 = new Date(now.getFullYear(), now.getMonth(), 1);
      start = ymdLocal(d1);
      end = ymdLocal(now);
    } else {
      start = from || "";
      end = to || "";
    }

    const params = new URLSearchParams();
    if (start) params.set("from", start);
    if (end) params.set("to", end);
    params.set("_", String(Date.now())); // cache-bust
    return `?${params.toString()}`;
  }, [range, from, to]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        const baseURL =
          window.location.hostname === "localhost"
            ? "http://localhost/shop/routes"
            : "https://shop.petrahcollection.co.ke/shop/routes";

        // 🔗 match PHP file exactly: dashboard-summary.php
        const res = await fetch(`${baseURL}/dashboard-summary.php${q}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          mode: "cors",
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}${txt ? `: ${txt}` : ""}`);
        }

        const data = await res.json();
        setSummary(data || {});
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        setError(err.message || "Failed to fetch");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [q]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <SkeletonCards />
        <SkeletonCharts />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-red-600 bg-red-50 border border-red-200 rounded-xl p-4 text-center">
        ⚠️ {error}
      </p>
    );
  }

  if (!summary || typeof summary.summary !== "object") {
    return (
      <p className="text-red-600 bg-red-50 border border-red-200 rounded-xl p-4 text-center">
        ⚠️ Invalid data returned from server.
      </p>
    );
  }

  const metrics = summary?.summary || {};
  const charts = summary?.charts || {};
  const tables = summary?.tables || {};

  // ----- charts (dynamic from PHP; fallback kept for safety) -----
  const salesChart = summary?.salesChart || charts?.salesTrend || { labels: [], datasets: [] };
  const stockChart = summary?.stockChart || charts?.stockByProduct || { labels: [], datasets: [] };
  const paymentSplit = charts?.paymentSplit || { labels: [], datasets: [] };
  const collectionsTrend = charts?.collectionsTrend || { labels: [], datasets: [] };

  // ----- dynamic payment methods from PHP -----
  const paymentsBreakdown = Array.isArray(metrics.paymentsBreakdown) ? metrics.paymentsBreakdown : [];
  const getMethodAmt = (name) => {
    const row = paymentsBreakdown.find(
      (p) => String(p.method || "").toLowerCase() === String(name).toLowerCase()
    );
    return Number(row?.amount || 0);
  };

  // Safe numbers (read explicit PHP fields first, then fallback to breakdown)
  const totals = {
    totalSales: Number(metrics.totalSales || 0),
    cashSales: Number(metrics.cashSales ?? getMethodAmt("Cash") ?? 0),
    momoSales: Number(metrics.momoSales ?? getMethodAmt("Momo") ?? 0),
    bankSales: Number(metrics.bankSales ?? getMethodAmt("Bank") ?? 0),
    creditSales: Number(metrics.creditSales ?? getMethodAmt("Credit") ?? 0),
    outstandingDebts: Number(metrics.outstandingDebts || 0),
    collected: Number(metrics.collected || 0),
    customers: Number(metrics.customers || metrics.totalCustomers || 0),
    aov: Number(metrics.aov || 0),
    unitsSold: Number(metrics.unitsSold || 0),
    stockPairs: Number(metrics.stockPairs || metrics.totalStock || 0),
    productsDistinct: Number(metrics?.productsCount?.distinct || 0),
    productsUnits: Number(metrics?.productsCount?.units || 0),
  };

  return (
    <div className="space-y-6">
      {/* Header + Date filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-gray-500">Overview for your shop (securely scoped to your login)</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
            {["today", "week", "month"].map((k) => (
              <button
                key={k}
                onClick={() => setRange(k)}
                className={clsx(
                  "px-3 py-1.5 text-sm rounded-lg",
                  range === k ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
                )}
              >
                {k === "today" ? "Today" : k === "week" ? "This Week" : "This Month"}
              </button>
            ))}
            <button
              onClick={() => setRange("custom")}
              className={clsx(
                "px-3 py-1.5 text-sm rounded-lg",
                range === "custom" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
              )}
            >
              Custom
            </button>
          </div>
          {range === "custom" && (
            <div className="flex items-center gap-2">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input" />
              <span className="text-gray-500">to</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input" />
              <button
                onClick={() => setRange("custom")}
                className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards — now reads explicit Momo & Bank fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard title="Total Sales" value={money(totals.totalSales)} accent="from-emerald-500/15 to-emerald-500/5" numberClass="text-emerald-600" icon={<CashIcon />} />
        <KpiCard title="Cash Sales" value={money(totals.cashSales)} accent="from-emerald-500/15 to-emerald-500/5" numberClass="text-emerald-700" icon={<BillsIcon />} />
        <KpiCard title="Momo Sales" value={money(totals.momoSales)} accent="from-sky-500/15 to-sky-500/5" numberClass="text-sky-600" icon={<MomoIcon />} />
        <KpiCard title="Bank Sales" value={money(totals.bankSales)} accent="from-indigo-500/15 to-indigo-500/5" numberClass="text-indigo-600" icon={<BankIcon />} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard title="Credit Sales" value={money(totals.creditSales)} accent="from-amber-500/15 to-amber-500/5" numberClass="text-amber-600" icon={<CreditIcon />} />
        <KpiCard title="Outstanding Debts" value={money(totals.outstandingDebts)} accent="from-rose-500/15 to-rose-500/5" numberClass="text-rose-600" icon={<DebtIcon />} />
        <KpiCard title="Collected This Period" value={money(totals.collected)} accent="from-teal-500/15 to-teal-500/5" numberClass="text-teal-600" icon={<CollectionIcon />} />
        <KpiCard title="Average Order Value" value={money(totals.aov)} accent="from-purple-500/15 to-purple-500/5" numberClass="text-purple-600" icon={<AOVIcon />} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <KpiCard title="Units Sold" value={pretty(totals.unitsSold)} accent="from-fuchsia-500/15 to-fuchsia-500/5" numberClass="text-fuchsia-600" icon={<UnitsIcon />} />
        <KpiCard title="Customers" value={pretty(totals.customers)} accent="from-gray-500/15 to-gray-500/5" numberClass="text-gray-700" icon={<UsersIcon />} />
        <KpiCard title="Stock Remaining" value={`${pretty(totals.stockPairs)} pairs`} accent="from-blue-500/15 to-blue-500/5" numberClass="text-blue-600" icon={<StockIcon />} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 2xl:grid-cols-3 gap-6">
        <ChartCard title="📈 Sales Trend">
          {salesChart?.labels?.length ? <Bar data={salesChart} height={280} /> : <EmptyMessage message="No sales data." />}
        </ChartCard>

        <ChartCard title="💳 Payment Split">
          {paymentSplit?.datasets?.length ? <Pie data={paymentSplit} height={280} /> : <EmptyMessage message="No payment data." />}
        </ChartCard>

        <ChartCard title="💵 Collections Timeline">
          {collectionsTrend?.labels?.length ? <Bar data={collectionsTrend} height={280} /> : <EmptyMessage message="No collections data." />}
        </ChartCard>
      </div>

      {/* Secondary charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title="🏷️ Revenue by Product (Top)">
          {charts?.revenueByProduct?.labels?.length ? <Bar data={charts.revenueByProduct} height={320} /> : <EmptyMessage message="No product revenue data." />}
        </ChartCard>
        <ChartCard title="📦 Units Sold by Product (Top)">
          {charts?.unitsByProduct?.labels?.length ? <Bar data={charts?.unitsByProduct} height={320} /> : <EmptyMessage message="No product units data." />}
        </ChartCard>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <TableCard
          title="🧾 Recent Sales"
          columns={["Time", "Customer", "Payment", "Amount"]}
          rows={(tables?.recentSales || []).map((r) => [
            fmtTime(r?.sold_at),
            r?.customer || r?.customer_name || "-",
            (r?.payment_type || "-")?.toUpperCase(),
            money(r?.total || 0),
          ])}
        />
        <TableCard
          title="👑 Top Customers"
          columns={["Customer", "Orders", "Spent"]}
          rows={(tables?.topCustomers || []).map((r) => [r?.customer || "-", pretty(r?.orders || 0), money(r?.spent || 0)])}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <TableCard
          title="⏳ Debts Aging"
          columns={["Bucket", "Unpaid"]}
          rows={(tables?.debtsAging || []).map((r) => [r?.bucket, money(r?.unpaid || 0)])}
        />
        <TableCard
          title="⚠️ Low-stock Alerts"
          columns={["Product", "Qty"]}
          rows={(tables?.lowStock || []).map((r) => [r?.name, pretty(r?.quantity || 0)])}
        />
      </div>

      {/* Optional: dynamic payment chips */}
      {paymentsBreakdown.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-base font-semibold mb-3">Payment Methods (This Period)</h3>
          <div className="flex flex-wrap gap-2">
            {paymentsBreakdown.map((p, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm">
                {p.method}: <strong className="ml-1">{money(Number(p.amount || 0))}</strong>{" "}
                <span className="text-gray-500">({Number(p.percent || 0)}%)</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Reusable pieces ---------- */

const KpiCard = ({ title, value, icon, numberClass = "text-gray-800", accent = "from-gray-500/10 to-gray-500/5" }) => {
  return (
    <div
      className={clsx(
        "relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm",
        "before:absolute before:inset-0 before:bg-gradient-to-br",
        accent
      )}
    >
      <div className="relative p-4 md:p-5 flex items-start gap-4">
        <div className="shrink-0 grid place-items-center h-11 w-11 rounded-xl bg-gray-900 text-white shadow">{icon}</div>
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{title}</p>
          <p className={clsx("mt-1 text-2xl font-semibold tabular-nums", numberClass)}>{value}</p>
        </div>
      </div>
    </div>
  );
};

const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
    <div className="flex items-center justify-between p-4 border-b border-gray-100">
      <h3 className="text-base font-semibold">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const TableCard = ({ title, columns = [], rows = [] }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between p-4 border-b border-gray-100">
      <h3 className="text-base font-semibold">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            {columns.map((c, i) => (
              <th key={i} className="px-4 py-2 text-left font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
      </table>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 ? (
            <tr>
              <td className="px-4 py-6 text-gray-400 italic" colSpan={columns.length}>
                No data.
              </td>
            </tr>
          ) : (
            rows.map((r, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {r.map((cell, j) => (
                  <td key={j} className="px-4 py-2">
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const EmptyMessage = ({ message }) => (
  <p className="text-sm text-gray-400 italic text-center py-10">{message}</p>
);

// Skeletons
const SkeletonCards = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="h-24 bg-gray-100 rounded-2xl" />
    ))}
  </div>
);

const SkeletonCharts = () => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
    <div className="h-80 bg-gray-100 rounded-2xl" />
    <div className="h-80 bg-gray-100 rounded-2xl" />
  </div>
);

// Icons
const CashIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M6 10h.01M18 14h.01M12 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
  </svg>
);
const BillsIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 7h18M3 17h18" />
    <rect x="5" y="9" width="14" height="6" rx="2" />
  </svg>
);
const CreditIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
);
const DebtIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12h8M12 8v8" />
  </svg>
);
const CollectionIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M7 13l3 3 7-7" />
  </svg>
);
const AOVIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3v18M3 12h18" />
  </svg>
);
const UnitsIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const StockIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 21h18" />
    <path d="M6 21V8l6-3 6 3v13" />
  </svg>
);
const MomoIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="14" rx="3" />
    <path d="M7 8h10M7 12h6" />
  </svg>
);
const BankIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 10l9-6 9 6" />
    <path d="M4 10h16v9H4z" />
    <path d="M9 14v5M12 14v5M15 14v5" />
  </svg>
);

function fmtTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString();
}

export default DashboardOverview;
